import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';

const OfferDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [offer, setOffer] = useState(null);
    const [app, setApp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);
    const [form, setForm] = useState({});

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 3000);
        return () => clearTimeout(t);
    }, [toast]);

    const load = async () => {
        try {
            setLoading(true);
            const { data: o, error } = await supabase
                .from('application_offers')
                .select('*')
                .eq('id', id)
                .single();
            if (error) throw error;
            setOffer(o);

            // Load related application
            if (o?.application_id) {
                const { data: a } = await supabase
                    .from('uni_applications')
                    .select('id, status, metadata, programs(name, universities(name)), intakes(term)')
                    .eq('id', o.application_id)
                    .single();
                setApp(a);
            }
        } catch (e) {
            console.error('Failed to load offer:', e);
            setToast({ type: 'error', message: 'Failed to load offer' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [id]);

    const startEdit = () => {
        setForm({
            offer_type: offer?.offer_type || 'conditional',
            issue_date: offer?.issue_date || '',
            expires_at: offer?.expires_at || '',
            tuition_total: offer?.tuition_total || '',
            scholarship_amount: offer?.metadata?.scholarship_amount || '',
            currency: offer?.metadata?.currency || '',
            deposit_amount: offer?.metadata?.deposit_amount || '',
            enrollment_deadline: offer?.metadata?.enrollment_deadline || '',
            notes: offer?.metadata?.notes || ''
        });
        setEditing(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await supabase.from('application_offers').update({
                offer_type: form.offer_type,
                issue_date: form.issue_date || null,
                expires_at: form.expires_at || null,
                tuition_total: form.tuition_total ? Number(form.tuition_total) : null,
                metadata: {
                    ...offer.metadata,
                    scholarship_amount: form.scholarship_amount ? Number(form.scholarship_amount) : null,
                    currency: form.currency || null,
                    deposit_amount: form.deposit_amount ? Number(form.deposit_amount) : null,
                    enrollment_deadline: form.enrollment_deadline || null,
                    notes: form.notes || null
                }
            }).eq('id', id);
            setEditing(false);
            await load();
            setToast({ type: 'success', message: 'Offer updated' });
        } catch (e) {
            setToast({ type: 'error', message: 'Failed to update offer' });
        } finally {
            setSaving(false);
        }
    };

    const handleAccept = async (accepted) => {
        try {
            await supabase.from('application_offers').update({
                accepted: !!accepted,
                accepted_at: accepted ? new Date().toISOString() : null
            }).eq('id', id);
            await load();
            setToast({ type: 'success', message: accepted ? 'Offer accepted' : 'Offer declined' });
        } catch {
            setToast({ type: 'error', message: 'Failed to update acceptance' });
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this offer?')) return;
        try {
            await supabase.from('application_offers').delete().eq('id', id);
            setToast({ type: 'success', message: 'Offer deleted' });
            navigate(-1);
        } catch {
            setToast({ type: 'error', message: 'Failed to delete offer' });
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-blue-400 font-mono animate-pulse">LOADING OFFER...</div>
            </div>
        );
    }

    if (!offer) {
        return (
            <div className="min-h-screen bg-gray-900 flex items-center justify-center">
                <div className="text-red-400 font-mono">Offer not found</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 font-mono text-white p-6">
            {/* Toast */}
            {toast && (
                <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded shadow-lg border ${toast.type === 'success' ? 'bg-green-900/90 border-green-700 text-green-100' : 'bg-red-900/90 border-red-700 text-red-100'}`}>
                    {toast.message}
                </div>
            )}

            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 border-b border-gray-800 pb-4">
                    <div>
                        <h1 className="text-2xl font-bold text-white mb-1">📄 Offer Details</h1>
                        <p className="text-gray-500 text-xs font-mono">ID: {id}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => navigate(-1)} className="px-4 py-2 rounded bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700 text-sm font-medium">
                            ← Back
                        </button>
                        {offer.case_id && (
                            <Link to={`/admin/cases/${offer.case_id}`} className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-500 border border-blue-500 text-sm font-medium">
                                View Case
                            </Link>
                        )}
                    </div>
                </div>

                {/* Main Content */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left Column - Offer Info */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Application Info */}
                        {app && (
                            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                                <h3 className="text-sm font-bold text-gray-400 uppercase mb-4">Related Application</h3>
                                <div className="space-y-2">
                                    <div className="text-white font-bold">{app.programs?.universities?.name || 'Unknown University'}</div>
                                    <div className="text-gray-300">{app.programs?.name || 'Unknown Program'}</div>
                                    {app.intakes?.term && <div className="text-gray-400 text-sm">Intake: {app.intakes.term}</div>}
                                    <span className={`inline-block px-2 py-1 rounded text-xs font-bold uppercase border ${app.status === 'submitted' ? 'bg-blue-900/30 text-blue-400 border-blue-800' : 'bg-gray-700 text-gray-400 border-gray-600'}`}>
                                        {app.status || 'draft'}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Offer Details */}
                        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-sm font-bold text-gray-400 uppercase">Offer Information</h3>
                                {!editing && (
                                    <button onClick={startEdit} className="px-3 py-1 rounded text-xs font-bold bg-blue-600 text-white border border-blue-500 hover:bg-blue-500">Edit</button>
                                )}
                            </div>

                            {editing ? (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Offer Type</label>
                                            <select className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white" value={form.offer_type} onChange={e => setForm({ ...form, offer_type: e.target.value })}>
                                                <option value="conditional">Conditional</option>
                                                <option value="unconditional">Unconditional</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Currency</label>
                                            <input className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} placeholder="USD" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Issue Date</label>
                                            <input type="date" className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white" value={form.issue_date} onChange={e => setForm({ ...form, issue_date: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Expires At</label>
                                            <input type="date" className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Tuition Total</label>
                                            <input type="number" className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white" value={form.tuition_total} onChange={e => setForm({ ...form, tuition_total: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Scholarship Amount</label>
                                            <input type="number" className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white" value={form.scholarship_amount} onChange={e => setForm({ ...form, scholarship_amount: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Deposit Amount</label>
                                            <input type="number" className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white" value={form.deposit_amount} onChange={e => setForm({ ...form, deposit_amount: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Enrollment Deadline</label>
                                            <input type="date" className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white" value={form.enrollment_deadline} onChange={e => setForm({ ...form, enrollment_deadline: e.target.value })} />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Notes</label>
                                        <textarea rows={3} className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => setEditing(false)} className="flex-1 px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600">Cancel</button>
                                        <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 disabled:opacity-50">{saving ? 'Saving...' : 'Save Changes'}</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    <div className="flex justify-between border-b border-gray-700 pb-2">
                                        <span className="text-gray-400">Type</span>
                                        <span className="text-white font-bold uppercase">{offer.offer_type || '-'}</span>
                                    </div>
                                    <div className="flex justify-between border-b border-gray-700 pb-2">
                                        <span className="text-gray-400">Issue Date</span>
                                        <span className="text-white">{offer.issue_date || '-'}</span>
                                    </div>
                                    <div className="flex justify-between border-b border-gray-700 pb-2">
                                        <span className="text-gray-400">Expires At</span>
                                        <span className="text-white">{offer.expires_at || '-'}</span>
                                    </div>
                                    <div className="flex justify-between border-b border-gray-700 pb-2">
                                        <span className="text-gray-400">Tuition Total</span>
                                        <span className="text-white">{offer.tuition_total ? `${offer.metadata?.currency || ''} ${offer.tuition_total}` : '-'}</span>
                                    </div>
                                    {offer.metadata?.scholarship_amount && (
                                        <div className="flex justify-between border-b border-gray-700 pb-2">
                                            <span className="text-gray-400">Scholarship</span>
                                            <span className="text-green-400">{offer.metadata.currency || ''} {offer.metadata.scholarship_amount}</span>
                                        </div>
                                    )}
                                    {offer.metadata?.deposit_amount && (
                                        <div className="flex justify-between border-b border-gray-700 pb-2">
                                            <span className="text-gray-400">Deposit Required</span>
                                            <span className="text-white">{offer.metadata.currency || ''} {offer.metadata.deposit_amount}</span>
                                        </div>
                                    )}
                                    {offer.metadata?.enrollment_deadline && (
                                        <div className="flex justify-between border-b border-gray-700 pb-2">
                                            <span className="text-gray-400">Enrollment Deadline</span>
                                            <span className="text-white">{offer.metadata.enrollment_deadline}</span>
                                        </div>
                                    )}
                                    {offer.metadata?.notes && (
                                        <div className="border-b border-gray-700 pb-2">
                                            <span className="text-gray-400 block mb-1">Notes</span>
                                            <p className="text-white text-sm">{offer.metadata.notes}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Column - Actions & Status */}
                    <div className="space-y-6">
                        {/* Acceptance Status */}
                        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                            <h3 className="text-sm font-bold text-gray-400 uppercase mb-4">Acceptance Status</h3>
                            <div className="text-center mb-4">
                                <span className={`inline-block px-4 py-2 rounded text-lg font-bold uppercase border ${offer.accepted ? 'bg-green-900/30 text-green-400 border-green-800' : 'bg-gray-700 text-gray-400 border-gray-600'}`}>
                                    {offer.accepted ? '✅ ACCEPTED' : 'PENDING'}
                                </span>
                                {offer.accepted_at && (
                                    <div className="text-xs text-gray-500 mt-2">On: {new Date(offer.accepted_at).toLocaleString()}</div>
                                )}
                            </div>
                            <div className="flex flex-col gap-2">
                                {!offer.accepted && (
                                    <button onClick={() => handleAccept(true)} className="w-full px-4 py-3 rounded font-bold bg-emerald-600 text-white hover:bg-emerald-500 border border-emerald-500">
                                        Accept Offer
                                    </button>
                                )}
                                {offer.accepted && (
                                    <button onClick={() => handleAccept(false)} className="w-full px-4 py-3 rounded font-bold bg-gray-700 text-white hover:bg-gray-600 border border-gray-600">
                                        Mark as Declined
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Danger Zone */}
                        <div className="bg-red-900/20 border border-red-800 rounded-lg p-6">
                            <h3 className="text-sm font-bold text-red-400 uppercase mb-4">Danger Zone</h3>
                            <button onClick={handleDelete} className="w-full px-4 py-3 rounded font-bold bg-red-900/50 text-red-400 hover:bg-red-900 border border-red-800">
                                Delete Offer
                            </button>
                        </div>

                        {/* Metadata */}
                        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                            <h3 className="text-sm font-bold text-gray-400 uppercase mb-4">Record Info</h3>
                            <div className="space-y-2 text-xs">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Created</span>
                                    <span className="text-gray-300">{offer.created_at ? new Date(offer.created_at).toLocaleString() : '-'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Updated</span>
                                    <span className="text-gray-300">{offer.updated_at ? new Date(offer.updated_at).toLocaleString() : '-'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OfferDetailPage;
